/* eslint-disable no-undef */
import React, { CSSProperties, FunctionComponent, useEffect, useRef } from 'react';
import { activateAdUnit, pullAdUnit, pushAdUnit, refreshAdUnits } from '../../services/adTags';
import { Container, Content } from './AdBox.style';

interface Props {
  id: string;
  url?: string;
  sizeKey: string;
  sizes: Array<number[]>;
  containerStyle?: CSSProperties;
  position?: string;
  hideUnder?: number;
  hideOver?: number;
  disabledPointerEvents?: boolean;
  testID?: string;
}

const AdBox: FunctionComponent<Props> = (props) => {
  const boxRef = useRef<HTMLDivElement>(null);
  const [width, height] = props.sizes[0];
  const inViewObserver = useRef<IntersectionObserver | null>(null);

  const onInView: IntersectionObserverCallback = (entries, observer) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        observer.disconnect();
        activateAdUnit(props.sizeKey, props.id);
        refreshAdUnits();
      }
    });
  };

  const onInRange: IntersectionObserverCallback = (entries, observer) => {
    entries.forEach(async (entry) => {
      if (entry.isIntersecting) {
        observer.disconnect();

        const ready = await pushAdUnit(props.sizeKey, props.id, props.sizes);

        if (ready) {
          inViewObserver.current = new IntersectionObserver(onInView, {
            rootMargin: `0px 0px ${height + 400}px 0px`,
            threshold: 1,
          });

          if (boxRef.current) inViewObserver.current?.observe(boxRef.current);
        }
      }
    });
  };

  useEffect(() => {
    const observer = new IntersectionObserver(onInRange, {
      rootMargin: `0px 0px ${height + 800}px 0px`,
      threshold: 1,
    });

    if (boxRef.current) observer.observe(boxRef.current);

    return () => {
      pullAdUnit(props.sizeKey, props.id);
      observer.disconnect();
      inViewObserver.current?.disconnect();
    };
  }, [props.id, props.url]);

  return (
    <Container
      ref={boxRef}
      hideUnder={props.hideUnder}
      hideOver={props.hideOver}
      position={props.position}
      style={props.containerStyle}
      disabledPointerEvents={props.disabledPointerEvents}
      data-testid={props.testID}
    >
      <Content
        id={`adunit-${props.sizeKey}${props.id}`}
        style={{
          maxWidth: width,
          height,
        }}
      />
    </Container>
  );
};

export default React.memo(AdBox);
